<script setup lang="ts">
import './assets/style.scss'
import { type Component, computed, type PropType, ref, watch } from 'vue'
import draggable from 'vuedraggable'
import { useRoute } from 'vue-router'
import VContentGridItemEmpty from './VContentGridItemEmpty.vue'
import type { ContentGridItemType } from '@/shared/ui'

const props = defineProps({
  columns: {
    type: Array as PropType<ContentGridItemType[]>,
    default: [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const route = useRoute()
const storageKey = `column_order_${route.params.type}`

const oneItems = ref<ContentGridItemType[]>([])
const twoItems = ref<ContentGridItemType[]>([])

const defaultOrder = {
  one: ['control', 'protect', 'meter'],
  two: ['events', 'tasks', 'map'],
}

/* Вычисляем сортировку */
const resultOrder = computed(() => {
  if (localStorage.getItem(storageKey)) {
    return JSON.parse(localStorage.getItem(storageKey) as string)
  } else {
    return defaultOrder
  }
})

/**
 * Функция сортировки и распределения элементов
 */
const sortColumns = () => {
  oneItems.value = []
  twoItems.value = []

  const availableColumns = [...props.columns]

  const sortedOne: ContentGridItemType[] = []
  const sortedTwo: ContentGridItemType[] = []

  resultOrder.value.one.forEach((name: string) => {
    const matchingIndex = availableColumns.findIndex((col) => col.component.name === name)
    if (matchingIndex !== -1) {
      sortedOne.push(availableColumns[matchingIndex])
      availableColumns.splice(matchingIndex, 1)
    }
  })

  resultOrder.value.two.forEach((name: string) => {
    const matchingIndex = availableColumns.findIndex((col) => col.component.name === name)
    if (matchingIndex !== -1) {
      sortedTwo.push(availableColumns[matchingIndex])
      availableColumns.splice(matchingIndex, 1)
    }
  })

  sortedTwo.push(...availableColumns)

  oneItems.value = sortedOne.length ? sortedOne : [{ component: VContentGridItemEmpty }]
  twoItems.value = sortedTwo.length ? sortedTwo : [{ component: VContentGridItemEmpty }]
}

watch(
  () => [route.params.object_id, props.columns],
  () => {
    sortColumns()
  },
  { immediate: true },
)

/**
 * Сохранение сортировки при изменении
 */
const changeHandler = () => {
  oneItems.value = oneItems.value.length
    ? oneItems.value.filter((item) => item.component.name !== 'empty')
    : [{ component: VContentGridItemEmpty }]
  twoItems.value = twoItems.value.length
    ? twoItems.value.filter((item) => item.component.name !== 'empty')
    : [{ component: VContentGridItemEmpty }]

  const order = {
    one: oneItems.value.map((item) => item.component.name),
    two: twoItems.value.map((item) => item.component.name),
  }

  localStorage.setItem(storageKey, JSON.stringify(order))
}
</script>

<template>
  <div class="v-content-grid">
    <div class="v-content-grid__column">
      <draggable
        v-model="oneItems"
        group="widgets"
        handle=".v-content-grid-item__move"
        @change="changeHandler"
      >
        <template #item="{ element }">
          <component
            :is="element.component"
            v-bind="element.props"
            :class="{ status_disabled: disabled }"
          />
        </template>
      </draggable>
    </div>

    <div class="v-content-grid__column v-content-grid__column_big">
      <draggable
        v-model="twoItems"
        group="widgets"
        handle=".v-content-grid-item__move"
        @update="changeHandler"
      >
        <template #item="{ element }">
          <component
            :is="element.component"
            v-bind="element.props"
            :class="{ status_disabled: disabled }"
          />
        </template>
      </draggable>
    </div>
  </div>
</template>
